import { GridItem } from '@components/GridItem';
import { HorizontalGrid } from '@components/HorizontalGrid';
import { LoadingBox } from '@components/LoadingBox';
import { Main } from '@components/Main';
import { PageContent } from '@components/PageContent';
import { PageTitle, PageTitleText } from '@components/PageTitle';
import { SaveButton } from '@components/Buttons/SaveButton';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { useValidation } from '@hooks/useValidation';
import { ClienteFormulario } from '@mod/Cliente/Detalhes/ClienteFormulario';
import { ICliente } from '@model/Cliente';
import { EnumClienteTipo, useClienteTipoCombo } from '@model/ClienteTipo';
import { useRemetenteWhatsappCombo } from '@model/RemetenteWhatsapp';
import { useClienteService } from '@services/ClienteService';
import { yup } from '@validations/Yup';
import { AxiosResponse } from 'axios';
import React from 'react';
import { useParams } from 'react-router-dom';
import { ClienteTab } from './ClienteTab';

export type ClienteDetalheParams = {
    id: string
}

export type ClienteDetalheProps = {
    
}

export function ClienteDetalhe (props: ClienteDetalheProps) {
    const [cliente, setCliente] = React.useState<ICliente>();

    const loading = useLoading();
    const message = useMessage();
    const clienteService = useClienteService();

    const remetenteWhatsappCombo = useRemetenteWhatsappCombo();
    const clienteTipoCombo = useClienteTipoCombo();

    const params = useParams<ClienteDetalheParams>();
    const clienteId = React.useMemo(() => parseInt(params.id), [params.id]);

    const form = useForm<Partial<ICliente>>({
        clienteId: useField(),

        clienteStatusId: useField(),
        clienteTipoId: useField(),
        enderecoId: useField(),
    
        nome: useField(),
        cpf: useField(),
        rg: useField(),
        email: useField(),
        celular: useField(),
        localTrabalho: useField(),
        indicadoPor: useField(),
        telefoneIndicacao: useField(),
        remetenteAviso: useField(),
        whatsappEnvio: useField(),
        spc: useField(),
        observacao: useField(),
        dataNascimento: useField(),
        cartaoSus: useField(),
        orgao: useField(), 
        cargo:  useField(),
        
        telefoneAlternativo: useField(),
        telefoneAlternativo2: useField(),
        telefoneAlternativo3: useField(),
        telefoneAlternativo4: useField(),

        clienteStatusNome: useField(),
        clienteTipoNome: useField(),
    
        enderecoLogradouro: useField(),
        enderecoBairro: useField(),
        enderecoCidade: useField(),
        enderecoNumero: useField(),
        enderecoComplemento: useField(),
        enderecoCep: useField(),
        enderecoUf: useField(),
        enderecoLat: useField(),
        enderecoLon: useField(),
        enderecoReferencia: useField()
    });

    const validation = useValidation<ICliente>({
        nome: yup.string().required(),
        rg: yup.string().when(["clienteTipoId"], {
            is: (clienteTipoId: EnumClienteTipo) => clienteTipoId === EnumClienteTipo.PessoaFisica,
            then: yup.string().required()
        }),
        whatsappEnvio: yup.string().required(),
        spc: yup.string().booleanString(),
        enderecoCep: yup.string().required(),
        enderecoLogradouro: yup.string().required(),
        enderecoBairro: yup.string().required(),
        enderecoCidade: yup.string().required(),
        enderecoUf: yup.string().required(),
    });

    const updateCliente = React.useCallback((response: AxiosResponse<ICliente>) => {
        setCliente(response.data)
        form.setValues(response.data);
    }, [form]);

    const handleSalvar = React.useCallback(() => {
        if (!loading.isLoading && cliente?.clienteId !== undefined) {
            const values = form.getInputValues();

            validation.validate(values)
                .then(() => {
                    loading.start();
                    clienteService.editar(cliente.clienteId, values)
                        .then(response => {
                            message.success("Dados do cliente salvos com sucesso");
                            updateCliente(response);
                        })
                        .catch(e => {
                            message.handleResponse(e)
                                .badRequest(form.setErrorsFromServer)
                                .showException();
                        })
                        .finally(loading.stop);
                })
                .catch(form.setErrorsFromYup);
        }
    }, [cliente?.clienteId, clienteService, form, loading, message, updateCliente, validation]);

    const carregar = React.useCallback(() => {
        if (!loading.isLoading) {
            loading.start();
            clienteService.carregar(clienteId)
                .then(updateCliente)
                .catch(message.showException)
                .finally(loading.stop);
        }
    }, [clienteId, clienteService, loading, message.showException, updateCliente]);

    useEffectOnce(() => {
        carregar();
        remetenteWhatsappCombo.loadOptions();
        clienteTipoCombo.loadOptions();
    });

    return (
        <Main>
            <LoadingBox isLoading={loading.isLoading}>
                <PageTitle>
                    <HorizontalGrid>
                        <GridItem>
                            <PageTitleText text="Clientes" />
                        </GridItem>
                        <GridItem floatRight>
                            <SaveButton onClick={handleSalvar} />
                        </GridItem>
                    </HorizontalGrid>
                </PageTitle>

                <PageContent>
                    <ClienteFormulario
                        form={form}
                        remetenteWhatsappCombo={remetenteWhatsappCombo}
                        clienteTipoCombo={clienteTipoCombo}
                    />

                    {
                        cliente !== undefined && (
                            <ClienteTab 
                                cliente={cliente}
                            />
                        )
                    }
                </PageContent>
            </LoadingBox>
        </Main>
    )
}